import React, { useState, useEffect } from 'react';
import { Measure as MeasureType, AppMode } from '../types';
import StyledChord from './StyledChord';
import MeasureAnnotations from './MeasureAnnotations';
import { FittedLyricsSimple, RepeatBar, GigMeasureContent } from './measureComponents';

const DoubleBar: React.FC = () => (
  <div className="h-full flex items-stretch gap-px min-w-[5px] flex-shrink-0">
    <div className="w-px bg-gray-400" />
    <div className="w-px bg-gray-400" />
  </div>
);

const FinalBar: React.FC = () => (
  <div className="h-full flex items-stretch gap-px min-w-[6px] flex-shrink-0">
    <div className="w-px bg-gray-400" />
    <div className="w-1.5 bg-gray-400" />
  </div>
);

export interface MeasureGridProps {
  mode: AppMode;
  measure: MeasureType;
  measureIndex: number;
  activeChord: any | null;
  isPlaying: boolean;
  playingChordIndex: number | null;
  isStartMeasure: boolean;
  onSetStartMeasure: (index: number) => void;
  onChordClick: (measureIndex: number, chordIndex: number) => void;
  onSubdivisionChange: (measureIndex: number, newSubdivision: number) => void;
  onPlayChord: (chord: string) => void;
  onCopyMeasure: (measureIndex: number) => void;
  onPasteMeasure: (measureIndex: number) => void;
  onChordHover: (chord: string, element: HTMLElement) => void;
  onChordLeave: () => void;
  onLyricChange: (measureIndex: number, newLyric: string) => void;
  timeSignature: any;
  invalidChords: Record<string, string | null>;
  clipboard: (string | null)[] | null;
  gigFontSize: number;
  lyricsFontSize?: number;
  gigChordScale?: number;
  containerHeight: number;
  inspectorMode?: 'piano' | 'guitar';
  inspectorEnabled?: boolean;
  chartHasFine?: boolean;
  chartHasToCoda?: boolean;
  voltaPosition?: import('./MeasureAnnotations').VoltaPosition;
  previousMeasureHasEndRepeat?: boolean;
}

const MeasureGrid: React.FC<MeasureGridProps> = ({ 
  mode, 
  measure, 
  measureIndex, 
  // activeChord,
  isPlaying,
  // playingChordIndex,
  isStartMeasure,
  onSetStartMeasure,
  onChordClick,
  // onSubdivisionChange,
  // onPlayChord,
  // onCopyMeasure,
  // onPasteMeasure,
  onChordHover,
  onChordLeave,
  onLyricChange,
  // timeSignature,
  invalidChords,
  // clipboard,
  gigFontSize,
  lyricsFontSize,
  gigChordScale = 1.0,
  containerHeight,
  inspectorMode = 'piano',
  inspectorEnabled = false,
  chartHasFine = false,
  chartHasToCoda = false,
  voltaPosition,
  previousMeasureHasEndRepeat = false,
}) => {
  const isGigMode = mode === 'gig';
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [inspectorButtonRef, setInspectorButtonRef] = useState<HTMLButtonElement | null>(null);

  // Detect mobile/tablet
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window;
      setIsMobileOrTablet(width <= 1024 || isTouchDevice);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Handle inspector button click
  const handleInspectorClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const firstChord = measure.chords[0];
    if (firstChord && firstChord.trim() && inspectorButtonRef) {
      onChordHover(firstChord, inspectorButtonRef);
    }
  };
  
  // Border styling based on state
  let borderClasses = 'border-gray-700';
  if (isPlaying) {
    borderClasses = 'border-cyan-400 ring-2 ring-cyan-400 ring-opacity-75';
  } else if (isStartMeasure) {
    borderClasses = 'border-green-500 ring-2 ring-green-500 ring-opacity-75';
  }

  const hasExplicitBarline = !!measure.annotations?.barlineLeft || !!measure.annotations?.barlineRight;
  const showStartRepeat = !!measure.annotations?.startRepeat;
  const showEndRepeat = !!measure.annotations?.endRepeat;
  const showLeadingDoubleBar = (measure.annotations?.barlineLeft === 'double' && !showStartRepeat) || (
    !hasExplicitBarline &&
    !!measure.annotations?.doubleBarline &&
    (!!measure.annotations?.ending || previousMeasureHasEndRepeat) &&
    !showStartRepeat
  );
  const showLeadingFinalBar = measure.annotations?.barlineLeft === 'final' && !showStartRepeat;
  const showTrailingDoubleBar = (measure.annotations?.barlineRight === 'double' && !showEndRepeat) || (
    !hasExplicitBarline && !!measure.annotations?.doubleBarline && !showLeadingDoubleBar && !showEndRepeat
  );
  const showTrailingFinalBar = (measure.annotations?.barlineRight === 'final' && !showEndRepeat) || (
    !hasExplicitBarline && !!measure.annotations?.finalBarline && !showEndRepeat
  );

  return (
    <div 
      className={`group ${isGigMode ? 'bg-[#5E5451]' : 'bg-gray-800'} rounded-lg shadow-md border flex flex-col transition-all duration-300 p-1 relative ${borderClasses}`}
      style={{ height: `${containerHeight}px` }}
      onClick={() => onSetStartMeasure(measureIndex)}
    >
      {/* Measure Number and Annotations - compact header in Gig Mode */}
      <div className="mb-1">
        <div className="flex justify-between items-center gap-2 mb-0.5 relative">
          {/* Ending bracket underline - positioned after ending numbers in Gig Mode */}
          {isGigMode && measure.annotations && (measure.annotations.ending || measure.annotations.multipleEnding) && (
            <div className={`absolute bottom-0 right-0 border-b border-gray-500 ${
              measure.annotations.section || measure.annotations.rehearsalLetter ? 'left-10' : 'left-4'
            }`}></div>
          )}
          
          <div
            className={`font-bold ${
              isGigMode 
                ? 'text-[7px] text-[rgba(218,222,224,0.5)] leading-none'
                : 'text-xs text-gray-500'
            }`}
          >
            {measureIndex + 1}
          </div>

          {/* Mobile/Tablet Inspector Button */}
          {isMobileOrTablet && inspectorEnabled && measure.chords[0]?.trim() && (
            <button
              ref={setInspectorButtonRef}
              onClick={handleInspectorClick}
              className="ml-1 w-8 h-8 flex items-center justify-center hover:bg-gray-700/30 rounded transition-colors"
              aria-label="Show chord diagram"
            >
              {inspectorMode === 'guitar' ? (
                <svg fill="#ff9500" viewBox="-62.4 -62.4 384.80 384.80" className="w-7 h-7">
                  <path d="M256.575,24.068L235.932,3.425c-1.9-1.9-4.981-1.9-6.881,0l-20.643,20.643c-1.899,1.899-1.9,4.979-0.002,6.879 l-57.178,57.178c-18.815-13.19-39.733-15.744-50.633-4.844c-2.47,2.47-4.249,5.456-5.371,8.815 c-0.952,2.787-2.043,5.646-3.387,8.497c-1.575,3.339-3.655,6.511-6.425,9.281c-3.879,3.879-8.567,6.389-13.444,7.956 c-10.148,3.26-22.628,5.638-33.076,8.792c-3.875,0.966-7.588,2.353-11.078,4.166c-1.686,0.828-3.216,1.715-4.546,2.678 c0.001,0.001,0.003,0.003,0.004,0.004c-2.486,1.667-4.828,3.579-6.995,5.745C6.647,148.846,1.985,161.96,2,176 c0.02,19.118,8.712,40.007,25.35,56.646C44.045,249.341,64.83,258.038,84,258c13.967-0.028,27.194-4.694,36.78-14.28 c2.178-2.178,4.097-4.533,5.77-7.033c0.932-1.292,1.793-2.767,2.597-4.392c1.884-3.602,3.32-7.438,4.298-11.451 c3.117-10.395,5.482-22.753,8.716-32.819c1.567-4.877,4.077-9.565,7.956-13.444c2.77-2.77,5.942-4.85,9.281-6.425 c2.806-1.324,5.62-2.4,8.366-3.341c3.413-1.119,6.448-2.909,8.951-5.412c10.901-10.901,8.346-31.819-4.844-50.634l57.178-57.178 l0.002,0.002c1.9,1.9,4.98,1.9,6.881,0l20.643-20.643C258.475,29.049,258.475,25.968,256.575,24.068z M84.605,209.906L50.2,175.501 l6.881-6.881l34.405,34.405L84.605,209.906z M135.618,150.183c-7.125,7.125-18.678,7.125-25.804,0s-7.125-18.678,0-25.804 c7.125-7.125,18.678-7.125,25.804,0S142.743,143.057,135.618,150.183z"/>
                </svg>
              ) : (
                <svg viewBox="-25.6 -25.6 115.20 115.20" className="w-7 h-7">
                  <path fill="#99c5ff" d="M60,0H4C1.789,0,0,1.789,0,4v56c0,2.211,1.789,4,4,4h56c2.211,0,4-1.789,4-4V4C64,1.789,62.211,0,60,0z M15,62H4c-1.104,0-2-0.896-2-2V4c0-1.104,0.641-2,2-2h7v39c0,0.553,0.447,1,1,1h3V62z M31,62H17V42h3c0.553,0,1-0.447,1-1V2h6v39 c0,0.553,0.447,1,1,1h3V62z M47,62H33V42h3c0.553,0,1-0.447,1-1V2h6v39c0,0.553,0.447,1,1,1h3V62z M62,60c0,1.104-0.896,2-2,2H49V42 h3c0.553,0,1-0.447,1-1V2h7c1.016,0,2,0.896,2,2V60z"/>
                </svg>
              )}
            </button>
          )}
          
          {/* In Gig Mode: Show annotations inline in header to save space */}
          {isGigMode && measure.annotations && (
            <div className="flex-1 flex items-center justify-center">
              <MeasureAnnotations 
                annotations={measure.annotations} 
                measureIndex={measureIndex}
                compact={true}
                fontSize={gigFontSize}
                chartHasFine={chartHasFine}
                chartHasToCoda={chartHasToCoda}
                voltaPosition={voltaPosition}
              />
            </div>
          )}
        </div>
        
        {/* Show full annotations only in Edition mode */}
        {!isGigMode && (
          <MeasureAnnotations annotations={measure.annotations} measureIndex={measureIndex} chartHasFine={chartHasFine} chartHasToCoda={chartHasToCoda} voltaPosition={voltaPosition} />
        )}
      </div>

      {/* Chord(s) with X divider system and inline repeat bars */}
      <div className="flex-grow flex items-stretch gap-0.5">
        {showLeadingFinalBar && <FinalBar />}

        {/* Leading double barline (used for start of ending sections) */}
        {showLeadingDoubleBar && <DoubleBar />}

        {/* Start repeat bar */}
        {showStartRepeat && <RepeatBar type="start" />}
        
        {/* Main chord area */}
        <div className="flex-grow relative rounded-sm overflow-hidden" style={{ backgroundColor: isGigMode ? 'transparent' : '#1F2937' }}>
          {isGigMode ? (
            <GigMeasureContent 
              measure={measure} 
              fontSize={gigFontSize}
              gigChordScale={gigChordScale}
              onChordClick={onChordClick}
              onChordHover={isMobileOrTablet ? () => {} : onChordHover}
              onChordLeave={onChordLeave}
              measureIndex={measureIndex}
              invalidChords={invalidChords}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              {measure.chords.map((chord, chordIndex) => (
                <div
                  key={chordIndex}
                  className="cursor-pointer hover:bg-gray-600/30 rounded p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChordClick(measureIndex, chordIndex);
                  }}
                  onMouseEnter={(e) => {
                    if (chord && !isMobileOrTablet) onChordHover(chord, e.currentTarget as HTMLElement);
                  }}
                  onMouseLeave={onChordLeave}
                >
                  {chord ? (
                    <StyledChord 
                      chord={chord} 
                      baseFontSize={gigFontSize} 
                      className={invalidChords[`${measureIndex}-${chordIndex}`] ? 'text-red-400' : 'text-gray-200'} 
                    />
                  ) : (
                    <span className="font-mono select-none text-gray-400 text-lg">/</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* End repeat bar */}
        {showEndRepeat && <RepeatBar type="end" />}

        {/* Trailing structural barlines */}
        {showTrailingDoubleBar && <DoubleBar />}
        {showTrailingFinalBar && <FinalBar />}
      </div>

      {/* Lyrics */}
      {measure.lyrics && (
        <div className="px-1 pb-1">
          <FittedLyricsSimple
            key={`lyrics-${measureIndex}-${lyricsFontSize}`}
            lyrics={measure.lyrics}
            onLyricChange={(newLyric) => onLyricChange(measureIndex, newLyric)}
            baseFontSize={lyricsFontSize || 14}
            measureIndex={measureIndex}
          />
        </div>
      )}
    </div>
  );
};

export default MeasureGrid;
